'use client';

import {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import type { ClientTaxReturnsProviderProps } from './ClientTaxReturnsProvider.types';
import { TaxReturn } from '@/_types/models/taxReturns';
import { UserContext } from '@/_contexts/UserProvider';
import { getTaxReturns } from '@/_api/client-tax-returns';
import { ClientDashboardContext } from '@/_contexts/client/ClientDashboardProvider';

const ClientTaxReturnsContext = createContext<ClientTaxReturnsProviderProps>({
  isClientLoading: false,
  filteredTaxReturns: [],
  allTaxReturns: [],
  _getTaxReturns: () => Promise.resolve(),
});

const ClientTaxReturnsProvider = (props: { children: ReactNode }) => {
  const { user, token } = useContext(UserContext);
  const { yearFilter } = useContext(ClientDashboardContext);
  const [taxReturns, setTaxReturns] = useState<Array<TaxReturn>>([]);
  const [isClientLoading, setClientLoading] = useState<boolean>(true);

  const filteredTaxReturns: Array<TaxReturn> = useMemo(() => {
    return taxReturns
      ? taxReturns
          ?.filter?.((tr) => tr.tax_year === Number(yearFilter))
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )
      : [];
  }, [yearFilter, taxReturns]);

  const _getEntityTaxReturns = useCallback(async () => {
    try {
      setClientLoading(true);

      const returns = await getTaxReturns({
        companyId: user?.default_company_id!,
        token,
      });

      setTaxReturns(returns);
    } catch (error) {
      console.error(error);
    } finally {
      setClientLoading(false);
    }
  }, [token, user?.default_company_id]);

  const _getTaxReturns = useCallback(async () => {
    if (!token || !user) return;

    await _getEntityTaxReturns();
  }, [user, token, _getEntityTaxReturns]);

  useEffect(() => {
    _getTaxReturns();
  }, [_getTaxReturns]);

  return (
    <ClientTaxReturnsContext
      value={{
        isClientLoading,
        filteredTaxReturns,
        allTaxReturns: taxReturns,
        _getTaxReturns,
      }}
    >
      {props.children}
    </ClientTaxReturnsContext>
  );
};

export { ClientTaxReturnsContext, ClientTaxReturnsProvider };
